import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { translations, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, getBrowserLanguage, getNestedValue } from '../i18n';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first
    const savedLang = localStorage.getItem('platemind_language');
    if (savedLang && SUPPORTED_LANGUAGES.find(l => l.code === savedLang)) {
      return savedLang;
    }
    // Then check browser language
    return getBrowserLanguage();
  });

  // Apply RTL direction for Arabic
  useEffect(() => {
    const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === language);
    if (langConfig) {
      document.documentElement.dir = langConfig.dir;
      document.documentElement.lang = language;
    }
  }, [language]);

  // Set language and save to localStorage
  const setLanguage = useCallback((newLang) => {
    if (SUPPORTED_LANGUAGES.find(l => l.code === newLang)) {
      setLanguageState(newLang);
      localStorage.setItem('platemind_language', newLang);
    }
  }, []);

  // Set language from user profile (after login)
  const setLanguageFromProfile = useCallback((profileLang) => {
    if (profileLang && SUPPORTED_LANGUAGES.find(l => l.code === profileLang)) {
      setLanguageState(profileLang);
      localStorage.setItem('platemind_language', profileLang);
    }
  }, []);

  // Translation function
  const t = useCallback((key, fallback = '') => {
    const currentTranslations = translations[language] || translations[DEFAULT_LANGUAGE];
    const value = getNestedValue(currentTranslations, key);
    
    if (value === null || value === undefined) {
      // Try English fallback
      const enValue = getNestedValue(translations[DEFAULT_LANGUAGE], key);
      return enValue !== null && enValue !== undefined ? enValue : fallback || key;
    }
    
    return value;
  }, [language]);

  // Get array of translations (for greeting messages, etc.)
  const tArray = useCallback((key) => {
    const currentTranslations = translations[language] || translations[DEFAULT_LANGUAGE];
    const value = getNestedValue(currentTranslations, key);
    
    if (Array.isArray(value)) {
      return value;
    }
    
    // Fallback to English
    const enValue = getNestedValue(translations[DEFAULT_LANGUAGE], key);
    return Array.isArray(enValue) ? enValue : [];
  }, [language]);

  const value = {
    language,
    setLanguage,
    setLanguageFromProfile,
    t,
    tArray,
    languages: SUPPORTED_LANGUAGES,
    isRTL: language === 'ar'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
